import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { alpha } from '@mui/material/styles';
import TableComponent from '../../../ui/data/table/TableComponent';
import ActionButton from '../../../ui/primitives/ActionButton';
import Loader from '../../../ui/primitives/Loader';
import HealthIndicator from './HealthIndicator';
import { getModuleCoverageInsights } from '../../../modules/dashboard';
import QUERY_KEYS from '../../../query/queryKeys';

const CoverageModuleHealthSection = forwardRef((props, ref) => {
  const { projectId } = useParams();
  const [selected, setSelected] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [order, setOrder] = useState('desc');
  const expandResolveRef = useRef(null);

  useImperativeHandle(ref, () => ({
    isExpanded: () => expanded,
    expandAndWait: () =>
      new Promise((resolve) => {
        if (expanded) {
          resolve();
          return;
        }
        expandResolveRef.current = resolve;
        setExpanded(true);
      }),
    collapse: () => setExpanded(false),
  }));

  const {
    data,
    isLoading: loading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: QUERY_KEYS.project.moduleCoverageInsights(projectId),
    queryFn: () => getModuleCoverageInsights(projectId),
    enabled: !!projectId,
  });

  const rows = useMemo(
    () =>
      (data?.modules || []).map((m) => ({
        id: m.module,
        name: m.module,
        tests: m.total_tests,
        automation: m.automation_coverage,
        traceability: m.traceability_coverage,
        executionCoverage: m.execution_coverage,
        highPriorityTests: m.high_priority_tests,
        health: m.health,
        healthScore: m.health_score,
      })),
    [data],
  );

  const headerTabs = useMemo(
    () => [
      { key: 'name', label: 'Module', minWidth: 180 },
      { key: 'tests', label: 'Tests Cases', minWidth: 90, sortable: true },
      { key: 'automation', label: 'Automation', minWidth: 110, formatContent: (row) => `${row.automation}%` },
      { key: 'traceability', label: 'Traceability', minWidth: 110, formatContent: (row) => `${row.traceability}%` },
      {
        key: 'executionCoverage',
        label: 'Executed (90d)',
        minWidth: 130,
        formatContent: (row) => `${row.executionCoverage}%`,
      },
      { key: 'highPriorityTests', label: 'High Priority', minWidth: 110, formatContent: (row) => `${row.highPriorityTests}` },
      {
        key: 'health',
        label: 'Health',
        minWidth: 150,
        sortable: true,
        sortKey: 'healthScore',
        formatContent: (row) => <HealthIndicator status={row.health} />,
      },
    ],
    [],
  );

  const disableRowHoverSx = {
    '& .MuiTableBody-root .MuiTableRow-root:hover': {
      backgroundColor: 'transparent !important',
    },
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disableGutters
      TransitionProps={{
        onEntered: () => {
          if (expandResolveRef.current) {
            expandResolveRef.current();
            expandResolveRef.current = null;
          }
        },
      }}
      sx={(theme) => ({
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: `${theme.shape.borderRadius}px !important`,
        overflow: 'hidden',
        '&:before': { display: 'none' },
      })}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 3,
          minHeight: 64,
          '& .MuiAccordionSummary-content': { my: 1.5 },
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Coverage & Module Health
        </Typography>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0 }}>
        {isError ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="error" action={<ActionButton label="Retry" onClick={() => refetch()} />}>
              {error?.message || 'Failed to load module coverage insights.'}
            </Alert>
          </Box>
        ) : loading ? (
          <Box sx={{ p: 2 }}>
            <Loader minHeight={240} />
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            <Box sx={disableRowHoverSx}>
              <TableComponent
                data={rows}
                headerTabs={headerTabs}
                tablekey="module coverage health"
                page={1}
                perPage={rows.length || 10}
                totalCount={rows.length}
                showCheckbox={false}
                showPages={false}
                selected={selected}
                setSelected={setSelected}
                order={order}
                setOrder={setOrder}
                isLoading={false}
              />
            </Box>
            {data?.insight && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: (t) => `${t.shape.borderRadius}px`,
                  bgcolor: (t) => alpha(t.palette.info.main, 0.06),
                  border: '1px solid',
                  borderColor: (t) => alpha(t.palette.info.main, 0.2),
                }}
              >
                <Typography variant="caption" fontWeight={700} color="info.dark" display="block" sx={{ mb: 0.25 }}>
                  AI Insight
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {data.insight}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
});

export default CoverageModuleHealthSection;